import type { components } from '@social-studio/api-client';
import { api } from '../../lib/api';
import { problemSlug, type Problem } from '../knowledge/problems';
import { BillingLoadError } from './billing-api';

export type Plan = components['schemas']['PlanDto'];
export type Subscription = components['schemas']['SubscriptionDto'];

/** `GET /v1/billing/plans` (S380-4): the monthly packages; empty for an exempt tenant. */
export async function fetchPlans(): Promise<Plan[]> {
  const { data, response } = await api.GET('/v1/billing/plans');
  if (!response.ok || !data) throw new BillingLoadError(response.status);
  return data.plans;
}

/** `GET /v1/billing/subscription` (S380-4, S380-5): trial and package of the active tenant. */
export async function fetchSubscription(): Promise<Subscription> {
  const { data, response } = await api.GET('/v1/billing/subscription');
  if (!response.ok || !data) throw new BillingLoadError(response.status);
  return data;
}

export type PlanFailure = 'exists' | 'planUnavailable' | 'noSubscription' | 'failed';
type Redirect = { readonly url: string } | { readonly failure: PlanFailure };

function failureOf(error: unknown): PlanFailure {
  switch (problemSlug(error as Problem | undefined)) {
    case 'subscription-exists':
      return 'exists';
    case 'plan-unavailable':
      return 'planUnavailable';
    case 'no-subscription':
      return 'noSubscription';
    default:
      return 'failed';
  }
}

/**
 * `POST /v1/billing/subscription/checkout`: Stripe Checkout for the first
 * package. `key` is the `Idempotency-Key`, so a repeated click opens the same session.
 */
export async function startPlanCheckout(plan: string, key: string): Promise<Redirect> {
  try {
    const { data, error, response } = await api.POST('/v1/billing/subscription/checkout', {
      params: { header: { 'Idempotency-Key': key } },
      body: { plan },
    });
    return response.ok && data ? { url: data.url } : { failure: failureOf(error) };
  } catch {
    return { failure: 'failed' };
  }
}

/** `POST /v1/billing/subscription/portal`: Stripe's customer portal for everything after the first booking. */
export async function openPlanPortal(): Promise<Redirect> {
  try {
    const { data, error, response } = await api.POST('/v1/billing/subscription/portal');
    return response.ok && data ? { url: data.url } : { failure: failureOf(error) };
  } catch {
    return { failure: 'failed' };
  }
}
